import styled, { css } from "styled-components/macro";
import {
  colorLightGray5,
  colorLightGray7,
  colorLightGray9,
  colorProductLink,
  colorProductPrimary,
  colorSecondaryHover,
  colorWhite,
  flexColumn,
  gridCenter,
} from "../../styles/globalVariables";
import { device } from "../../styles/mediaQueries";
import { AccordionBody, ActiveProp, IOnClick } from "../../styles/interfaces";
import { TextLink } from "../../styles/Shared";
import { isRTLLanguage } from "../../util/functions";

/**
 * HeaderMenu
 */

export const StyledHeaderMenu = styled.header`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-inline: auto;
  background-color: rgb(${colorWhite});
  position: sticky;
  top: 0;
  z-index: 5;
`;

export const Top = styled.div`
  display: flex;
  border-bottom: 1px solid rgb(${colorLightGray5});
  height: 3.25rem;
  flex-direction: ${() => {
    const isRTL = isRTLLanguage(document.documentElement.lang || 'en');
    return isRTL ? 'row-reverse' : 'row';
  }};
`;

export const LogoContainer = styled.div`
  margin-left: 0.5rem;
  ${flexColumn}
  align-items: flex-start;
  h2 {
    color: rgb(${colorProductLink});
    font-size: 0.75rem;
  }
`;

export const ContentMain = styled.main<any>`
  ${(props) => {
    const isRTL = isRTLLanguage(document.documentElement.lang || 'en');
    
    if (isRTL) {
      return css`
        margin-right: ${props.isActive ? "13.4rem" : "4.5rem"};
        margin-left: 0;
        width: ${props.isActive ? "calc(100% - 13.4rem)" : "calc(100% - 4.5rem)"};
      `;
    } else {
      return css`
        margin-left: ${props.isActive ? "13.4rem" : "4.5rem"};
        margin-right: 0;
        width: ${props.isActive ? "calc(100% - 13.4rem)" : "calc(100% - 4.5rem)"};
      `;
    }
  }}
  
  transition: all 0.5s ease;
  display: flex;
  max-height: calc(100vh - 5.5rem);
  min-height: calc(100vh - 5.5rem);
  overflow: auto;
  ${(props) => props.theme.bgColorStyleForLoggedInPage};
  
  @media ${device.desktop} {
    ${(props) => {
      const isRTL = isRTLLanguage(document.documentElement.lang || 'en');
      
      if (isRTL) {
        return css`
          margin-right: ${props.isActive ? "13.4rem" : "4.5rem"};
          margin-left: 0;
        `;
      } else {
        return css`
          margin-left: ${props.isActive ? "13.4rem" : "4.5rem"};
          margin-right: 0;
        `;
      }
    }}
    flex-direction: column;
  }
  
  @media ${device.tablet} {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
`;
/**
 * MenuTop
 */

export const EmailContainer = styled.div`
  ${gridCenter}
  margin-left: auto;
  margin-right: 2rem;
`;

interface IHasMarginLeft {
  marginLeft?: boolean;
  isRTL?: boolean;
}

export const Dropdown = styled.div<IHasMarginLeft>`
  ${(p) => {
    if (p.isRTL) {
      return css`
        margin-right: ${p.marginLeft ? "none" : "auto"};
        margin-left: 0;
      `;
    } else {
      return css`
        margin-left: ${p.marginLeft ? "none" : "auto"};
        margin-right: 0;
      `;
    }
  }}
  display: flex;
  align-items: center;
  position: relative;
  & > button {
    ${(p) => {
      if (p.isRTL) {
        return css`
          margin-left: 1.25rem;
          margin-right: 0;
        `;
      } else {
        return css`
          margin-right: 1.25rem;
          margin-left: 0;
        `;
      }
    }}
    ${gridCenter}
  }
`;

export const UserContainer = styled.div`
  ${flexColumn}
  align-items: flex-end;
  margin-right: 0.625rem;
`;

export const Name = styled.p`
  font-size: 0.875rem;
  width: 12.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  line-height: 1.4rem;

  @media ${device.tablet} {
    font-size: 0.75rem;
    width: 10rem;
  }
`;

export const Role = styled.p`
  font-size: 0.75rem;
  font-weight: 300;

  @media ${device.tablet} {
    font-size: 0.65rem;
  }
`;

export const SignOutButton = styled.button`
  padding: 0 1.875rem;
  ${(props) => props.theme.themeTextStyleColor}
  border-left: 1px solid rgb(${colorLightGray5});
  font-size: 0.875rem;
  background-color: rgb(${colorWhite});
  transition: all 0.3s ease;

  &:hover {
    background-color: rgb(${colorSecondaryHover});
  }

  @media ${device.tablet} {
    font-size: 12px;
  }
`;

/**
 * MenuBottom
 */

export const Bottom = styled.div`
  display: flex;
  height: 3rem;
  align-items: center;
`;

export const MenuButtonContainer = styled.div`
  ${flexColumn}
  padding-inline: 1.25rem;
  border-right: 3px solid ${(props) => props.theme.secondary1};
  border-bottom: 1px solid ${(props) => props.theme.secondary1};
  height: 100%;
  width: 4.5rem;
  background: ${(props) => props.theme.secondary1};
`;

export const Location = styled.h2`
  font-weight: 600;
  font-size: 1.25rem;
  color: ${(props) => props.theme.breadcrumbTextColor};
  // margin-left: 1.25rem;

  @media ${device.tablet} {
    margin-left: 4px;
    margin-right: 4px;
  } 
`;

export const DynamicContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  border-bottom: 1px solid rgb(${colorLightGray5});
`;

export const DynamicButtonContainer = styled.div`
  display: flex;
  margin-right: 1.8125rem;
  gap: 1.25rem;
`;

export const MenuButton = styled.button<IOnClick>`
  ${flexColumn}
  font-size: .625rem;
  color: white;
  & > svg {
    transform: rotate(${(p) => (p.isActive ? "180deg" : "0")});
  }
`;

export const BreadcumbsContainer = styled.div``;

export const BreadCrumbs = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  ${TextLink} {
    font-size: 0.75rem;
  }
  margin-left: 1.25rem;
  gap: 5px;
  color: rgb(${colorLightGray9});

  & > svg {
    height: 0.625rem;
    width: 0.625rem;
    color: rgb(${colorLightGray7});
  }
`;

/**
 * Sidebar
 */
export const StyledSidebar = styled.div<ActiveProp>`
  ${(props) => props.theme.bgPrimaryStyleStriveColor}
  ${flexColumn}
  justify-content: flex-start;
  position: fixed;
  top: 0;
  transition: all 0.5s ease;
  height: 100vh;
  width: ${(p) => (p.isActive ? "13.4rem" : "4.5rem")};
  overflow-x: hidden; // Hide horizontal scrollbar
  overflow-y: auto; // Enable vertical scrollbar
  @media ${device.tablet} {
    width: ${(p) => (p.isActive ? "50%" : "0")};
    overflow: hidden;
    position: absolute;
    z-index: 2;
  }
`;

export const LinkContainer = styled.div<ActiveProp>`
  color: rgb(${colorWhite});
  width: 100%;
  ${flexColumn}
  gap: .625rem;
  padding-top: ${(p) => (p.isActive ? "8.5rem" : "14rem")};
  overflow-y: auto;
  @media ${device.tablet} {
    display: ${(p) => (p.isActive ? "flex" : "none")};
    justify-content: flex-start;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: ${(props) => props.theme.bgColor};
    padding-top: 8rem;
    z-index: 2;
  }
  &::-webkit-scrollbar {
    width: 4.5px;
  }
`;
export const AccordionItem = styled.div`
  width: 100%;
  /* &:not(:last-child) {
    border-bottom: 1px solid black;
  } */
`;

export const AccBody = styled.div<AccordionBody>`
  display: block;
  position: relative;
  margin: 0;
  height: 0;
  overflow: hidden;
  transition: height 0.3s;
  width: 100%;

  ${({ active }) =>
    active &&
    css`
      height: fit-content;
    `}
`;

export const SideButton = styled.button<ActiveProp>`
  ${flexColumn}
  width: 100%;
  gap: ${(p) => (p.isActive ? "0.4rem" : 0)};
  padding: 7px 2px;
  background-color: ${(p) =>
    p.isSelected
      ? (props) => props.theme.secondary1
      : (props) => props.theme.whiteAppColor};
  color: ${(p) =>
    p.isSelected
      ? (props) => props.theme.secondary1Text
      : (props) => props.theme.whiteAppColor};
  justify-content: ${(p) => (p.isActive ? "flex-start" : "center")};
  padding-left: ${(p) => (p.isActive ? "1rem" : "")};
  flex-direction: ${(p) => (p.isActive ? "row" : "column")};
  position: relative;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? "" : props.theme.sidebarBtnHoverBgColor};
    color: ${(props) =>
      props.isSelected ? "" : props.theme.sidebarBtnHoverTextColor};
  }

  @media ${device.tablet} {
    flex-direction: column;
    padding-left: 0;
    justify-content: center;
  }
`;
export const SideGroupButton = styled.button<ActiveProp>`
  ${flexColumn}
  width: 100%;
  gap: ${(p) => (p.isActive ? "0.4rem" : 0)};
  padding: 7px 2px;
  background-color: ${(p) =>
    p.isSelected
      ? (props) => props.theme.whiteAppColor
      : (props) => props.theme.secondary1};
  color: ${(p) =>
    p.isSelected
      ? (props) => props.theme.secondary1
      : (props) => props.theme.whiteAppColor};
  justify-content: ${(p) => (p.isActive ? "flex-start" : "center")};
  padding-left: ${(p) => (p.isActive ? "1rem" : "")};
  flex-direction: ${(p) => (p.isActive ? "row" : "column")};
  position: relative;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? "" : props.theme.sidebarBtnHoverBgColor};
    color: ${(props) =>
      props.isSelected ? "" : props.theme.sidebarBtnHoverTextColor};
  }

  @media ${device.tablet} {
    flex-direction: row;
    padding-left: 0;
    justify-content: center;
    padding: 0.5rem;
  }

  &:after {
    display: none;
  }

  &:not(.collapsed) {
    color: ${(p) =>
      p.isSelected
        ? (props) => props.theme.whiteAppColor
        : (props) => props.theme.secondary1};
    background-color: ${(p) =>
      p.isSelected
        ? (props) => props.theme.secondary1
        : (props) => props.theme.whiteAppColor};
    box-shadow: none;
    overflow: hidden;
  }
`;

export const SideGroupSubButton = styled.button<ActiveProp>`
  ${flexColumn}
  width: 100%;
  gap: ${(p) => (p.isActive ? "0.4rem" : 0)};
  padding: 7px 2px;
  font-weight: 400;
  background-color: #fafafa;
  color: ${(p) =>
    p.isSelected ? (props) => props.theme.primaryAppColor : "#7D7D7D"};
  justify-content: ${(p) => (p.isActive ? "flex-start" : "center")};
  padding-left: ${(p) => (p.isActive ? "1.1rem" : "")};
  flex-direction: ${(p) => (p.isActive ? "row" : "column")};
  position: relative;

  &:hover {
    background-color: ${(props) => props.theme.sidebarBtnHoverBgColor};
    color: ${(props) => props.theme.sidebarBtnHoverTextColor};
  }

  @media ${device.tablet} {
    flex-direction: row;
    padding-left: 0;
    display: flex;
    align-items: flex-start;
    padding: 0.75rem;
  }
`;

export const AccordionUl = styled.ul`
  &.show {
    visibility: visible
  }
}
`;

export const SideGroupButtonTitle = styled.p<ActiveProp>`
  font-size: ${(p) => (p.isActive ? "14px" : "10px")};
  text-transform: capitalize;
  font-weight: 400;
  text-align: ${(p) => (p.isActive ? "left" : "center")};

  @media ${device.tablet} {
    font-size: 12px;
  }
`;

export const ChildButton = styled.button<ActiveProp>`
  ${flexColumn}
  width: 100%;
  gap: 0.4rem;
  padding: 0.4rem 0;
  background-color: ${(p) => (p.isSelected ? "rgb(255, 255, 255)" : "#99B0C6")};
  color: ${(p) => (p.isSelected ? "rgb(44,113,173)" : "black")};
  justify-content: ${(p) => (p.isActive ? "flex-end" : "flex-end")};
  flex-direction: ${(p) => (p.isActive ? "row" : "column")};
  position: static;

  &:hover {
    background-color: rgb(${colorWhite});
    color: rgb(${colorProductPrimary});
  }

  @media ${device.tablet} {
    flex-direction: column;
    padding-left: 0;
    justify-content: center;
  }
`;

export const SideButtonTitle = styled.p<ActiveProp>`
  font-size: ${(p) => (p.isActive ? "14px" : "10px")};
  text-transform: capitalize;
  font-weight: 600;
  text-align: ${(p) => (p.isActive ? "left" : "center")};

  @media ${device.tablet} {
    font-size: 0.75rem;
  }
`;

export const DropdownButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  &:hover {
    ${(props) => props.theme.secondaryHoverBgColor}
    border-radius: 50%;
  }
`;

export const DropdownItem = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 150px;
  height: 40px;
  top: 46px;
  background: #ffffff;
  border: 1px solid #bfbfbf;
  box-shadow: 0px 9px 28px 8px rgb(0 0 0 / 5%), 0px 6px 16px rgb(0 0 0 / 8%),
    0px 3px 6px -4px rgb(0 0 0 / 12%);
  border-radius: 6px;
  font-family: "Open Sans";
  font-size: 14px;
  line-height: 22px;
  color: #262626;
  justify-content: center;
  padding: 10px;
  right: 20px;
  & > a {
    cursor: pointer;
  }
`;

export const VersionDiv = styled.div<ActiveProp>`
  margin-top: auto;
  text-align: ${(p) => (p.isActive ? "left" : "center")};
  width: 100%;
  padding-left: ${(p) => (p.isActive ? "1.4rem" : "0px")};
  padding-bottom: 0.5rem;
  color: white;
  font-size: 12px;
  font-weight: 400;
`;
